// LES AFFICHES DE BAUHAUS (sans GPU, testable) : les types de formes et leur
// champ de distance, les 16 affiches (12 refaites + 4 de style), le
// remplisseur de micro-éléments, le générateur, l'analyse (vide, éléments).
//
// Unités : la COLONNE de la grille (0,2797 unité de hauteur en 16:9 ; l'écran
// fait ±6,36 x ±3,58 colonnes), y vers le haut, centre = centre de
// composition (R19). La case de la pochette (±1,75 x ±2,35) est réservée.

export const COL = 0.2797;
export const SCREEN = [6.36, 3.575];        // le demi-écran 16:9, en colonnes
export const RESERVE = [1.75, 2.35];        // la case de la pochette
// les types : code GPU, famille (l'instrument), taille de « micro »
export const TYPES = {
  C: 0,   // disque (w = diamètre)
  R: 1,   // anneau fin (p1 = épaisseur en px)
  O: 2,   // cercles concentriques (p1 = nombre)
  H: 3,   // secteur plein (p2 = ouverture en rad ; 3,14 = demi-disque)
  A: 4,   // arc (p1 = épaisseur en px, p2 = ouverture)
  Q: 5,   // quart de disque (w = rayon, le coin au centre)
  B: 6,   // barre / rectangle (w x h)
  T: 7,   // triangle isocèle (base w, hauteur h)
  S: 8,   // carré (w)
  L: 9,   // ligne fine (w = longueur, p1 = épaisseur en px)
  D: 10,  // rangée de points (w = longueur, h = diamètre d'un point, p1 = nombre)
  K: 11,  // damier 4 x 4 (w)
  X: 12,  // hachures (w x h, p1 = nombre de traits)
  P: 13,  // croix (w, p1 = épaisseur px)
  F: 14,  // flèche (w = longueur, p1 = épaisseur px)
  G: 15   // lettre (atlas)
};
export const INK_KEYS = ['Y', 'R', 'B', 'K', 'A'];    // dans l'ordre de l'impression (PRINT)

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
export function rng(seed) { let s = (seed >>> 0) || 1; return () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; }; }

// la famille musicale d'une forme (le coeur de la refonte)
export function familyOf(s) {
  if (s.type === 'C' && s.w >= 1.8) return 'kick';
  if (s.type === 'B' && Math.max(s.w, s.h) / Math.max(1e-3, Math.min(s.w, s.h)) >= 4 && s.ink !== 'K') return 'bass';
  if (s.type === 'T') return 'snare';
  if (s.type === 'D') return 'hats';
  if (s.type === 'H' || s.type === 'A') return 'melody';
  if (s.type === 'S' && s.w <= 0.9) return 'accent';
  return null;
}
export function isMicro(s) {
  if (s.type === 'G') return false;
  if (['L', 'D', 'K', 'X', 'P', 'F'].includes(s.type)) return true;
  if (s.type === 'O' || s.type === 'R' || s.type === 'A') return s.w <= 1.6;
  return Math.max(s.w, s.h || 0) <= 0.9;
}
export function isLarge(s) { return s.type !== 'G' && s.type !== 'L' && Math.max(s.w, s.h || 0) >= 3.0; }
export function isThickDiagonalRule(s) { return s.type === 'B' && s.ink === 'K' && Math.min(s.w, s.h) >= 0.25 && Math.max(s.w, s.h) >= 4 && Math.abs(((s.rot % 90) + 90) % 90) >= 15; }

// le champ de distance (colonnes), < 0 dedans ; pxCol : pixels par colonne (les traits en px)
export function sdf(s, px, py, pxCol) {
  const pc = pxCol || 75;
  const a = -s.rot * Math.PI / 180, c = Math.cos(a), sn = Math.sin(a);
  const x = (px - s.x) * c - (py - s.y) * sn, y = (px - s.x) * sn + (py - s.y) * c;
  const box = (bx, by, xx, yy) => { const qx = Math.abs(xx) - bx, qy = Math.abs(yy) - by; return Math.hypot(Math.max(qx, 0), Math.max(qy, 0)) + Math.min(Math.max(qx, qy), 0); };
  const r = Math.hypot(x, y), R = s.w / 2;
  const sector = (open) => { const ang = Math.abs(Math.atan2(x, y)); return ang <= open / 2 ? 0 : 1; };
  switch (s.type) {
    case 'C': return r - R;
    case 'R': return Math.abs(r - R) - (s.p1 || 2) / pc / 2;
    case 'O': { const n = s.p1 || 3, step = R / n; const k = Math.min(n, Math.max(1, Math.round(r / step))); return Math.abs(r - k * step) - 1.2 / pc + (r > R + 2 / pc ? r - R : 0); }
    case 'H': return sector(s.p2 || Math.PI) ? Math.max(r - R, 0.02) + 0.02 : r - R;
    case 'A': return sector(s.p2 || Math.PI) ? 1 : Math.abs(r - R) - (s.p1 || 3) / pc / 2;
    case 'Q': return Math.max(r - s.w, Math.max(-x, -y));
    case 'B': return box(s.w / 2, s.h / 2, x, y);
    case 'S': return box(s.w / 2, s.w / 2, x, y);
    case 'T': { const hw = s.w / 2, hh = s.h / 2, nl = Math.hypot(s.h, hw); return Math.max(-y - hh, (Math.abs(x) * s.h + (y - hh) * hw) / nl); }
    case 'L': return box(s.w / 2, (s.p1 || 2) / pc / 2, x, y);
    case 'D': { const n = s.p1 || 6, step = s.w / Math.max(1, n - 1); const k = Math.max(0, Math.min(n - 1, Math.round((x + s.w / 2) / step))); return Math.hypot(x - (-s.w / 2 + k * step), y) - s.h / 2; }
    case 'K': { const cell = s.w / 4; if (Math.abs(x) > s.w / 2 || Math.abs(y) > s.w / 2) return box(s.w / 2, s.w / 2, x, y); const i = Math.floor((x + s.w / 2) / cell), j = Math.floor((y + s.w / 2) / cell); return (i + j) % 2 ? 0.01 : -0.01; }
    case 'X': { if (Math.abs(x) > s.w / 2 || Math.abs(y) > s.h / 2) return box(s.w / 2, s.h / 2, x, y); const n = s.p1 || 6, step = s.w / n; const f = ((x + s.w / 2) % step + step) % step; return Math.abs(f - step / 2) - 1.5 / pc / 2; }
    case 'P': return Math.min(box(s.w / 2, (s.p1 || 3) / pc / 2, x, y), box((s.p1 || 3) / pc / 2, s.w / 2, x, y));
    case 'F': { const t = (s.p1 || 3) / pc / 2; const shaft = box(s.w / 2, t, x, y); const hx = x - s.w / 2; return Math.min(shaft, Math.max(Math.abs(hx + Math.abs(y) * 1.0) - t * 1.5, -(s.w * 0.25) - hx)); }
    case 'G': return box(s.w / 2, s.h / 2, x, y);
  }
  return 1;
}
export function radiusOf(s) {
  if (s.type === 'Q') return s.w * Math.SQRT2;
  if (['C', 'R', 'O', 'H', 'A', 'K', 'S'].includes(s.type)) return s.w * (s.type === 'K' || s.type === 'S' ? 0.71 : 0.5);
  return Math.hypot(s.w, s.h || 0.1) / 2;
}
// une forme n'entre pas dans la case de la pochette (échantillons de son intérieur)
export function clearOfReserve(s) {
  const r = radiusOf(s) + 0.05;
  if (Math.abs(s.x) - r > RESERVE[0] || Math.abs(s.y) - r > RESERVE[1]) return true;
  for (let j = 0; j <= 20; j++) for (let i = 0; i <= 20; i++) {
    const x = s.x - r + 2 * r * i / 20, y = s.y - r + 2 * r * j / 20;
    if (Math.abs(x) < RESERVE[0] && Math.abs(y) < RESERVE[1] && sdf(s, x, y) < 0) return false;
  }
  return true;
}

// ------------------------------------------------------------ les affiches
// Une ligne par forme : type cx cy w h rot encre [niveau] [p1] [p2]
// (niveau 0 : encre imprimée ; 1 et 2 : papier découpé collé, avec ombre).
// `title` : la typographie monumentale (départ, hauteur des lettres, angle).
// `style` : ce que le remplisseur ajoute (micro-éléments).
const STRUCT = [
  { name: 'kandinsky-rouge', style: 'kandinsky', title: { x: -6.1, y: -3.3, h: 2.2, rot: 90, ink: 'K' },
    s: `C -3.6 0.7 4.2 0 0 B 0 | C -3.6 0.7 1.6 0 0 Y 1 | B 4.3 -0.6 5.4 0.5 -30 R 0 | T 4.2 1.9 2.2 1.9 0 Y 0 | H 4.4 -2.7 2.4 0 0 B 2 0 3.1416 | B -0.2 -2.9 6.5 0.32 -15 K | B 3.3 2.9 3.8 0.42 0 K 0 | Q -2.4 -3.6 2.0 0 0 R 0 | A 3.0 0.4 2.8 0 0 K 0 3 2.2 | S 2.6 -1.9 0.7 0 15 R 1` },
  { name: 'triangles', style: 'suisse', title: { x: 2.2, y: 3.15, h: 1.5, rot: 0, ink: 'R' },
    s: `T 4.0 -0.4 4.6 4.0 0 Y 0 | Q -6.4 -3.6 4.0 0 0 R 0 | C -3.4 1.6 2.4 0 0 B 0 | B -3.2 -1.2 6.0 0.3 30 K | B 3.8 -3.0 4.2 0.5 0 B 0 | O 4.9 2.4 1.6 0 0 K 0 4 | H -2.5 -2.6 1.8 0 180 Y 1 0 3.1416 | S -5.4 0.4 0.8 0 45 R 1 | A -3.4 1.6 3.2 0 0 R 0 2 2.6` },
  { name: 'colonne', style: 'suisse', title: { x: -6.0, y: 2.6, h: 1.7, rot: -90, ink: 'K' },
    s: `C 3.6 1.4 3.0 0 0 B 0 | C 3.6 -1.4 2.2 0 0 R 0 | C 3.6 -3.2 1.2 0 0 Y 0 | B -3.4 0 0.7 7.4 0 K 0 | H -4.6 1.8 2.6 0 90 R 2 0 3.1416 | T -4.8 -2.0 2.0 1.7 180 Y 0 | B 0 2.95 4.2 0.3 0 R 0 | B 3.0 0.2 6.4 0.3 -60 K | Q 6.4 3.6 1.8 0 180 Y 1` },
  { name: 'diagonale', style: 'constructiviste', title: { x: -6.0, y: -2.6, h: 1.6, rot: 30, ink: 'R' },
    s: `B -3.8 0.2 6.4 1.1 -45 R 0 | C 4.4 0.0 4.0 0 0 K 0 | C 4.4 0.0 1.6 0 0 Y 2 | B 3.6 -2.8 5.4 0.32 15 K | T 2.9 2.6 1.8 1.4 0 R 0 | H -2.5 -2.8 1.9 0 0 Y 0 0 3.1416 | Q 6.4 -3.6 1.6 0 90 B 1 | S -5.4 2.8 0.8 0 30 R 1` },
  { name: 'echo', style: 'kandinsky', title: { x: 2.3, y: 3.1, h: 1.4, rot: 0, ink: 'K' },
    s: `H -3.8 -1.6 5.2 0 0 B 0 0 3.1416 | H -3.8 -0.9 3.4 0 0 Y 0 0 3.1416 | H -3.8 -0.3 1.6 0 0 R 0 0 3.1416 | B 3.8 0.9 5.0 0.6 15 K | C 4.5 -1.8 2.6 0 0 R 0 | B -3.9 -1.65 6.0 0.16 0 K 0 | T 2.8 -3.2 1.4 1.1 0 B 1 | A 4.5 -1.8 3.4 0 0 K 0 2 3.6 | S -1.0 3.0 0.7 0 0 R 1` },
  { name: 'croix', style: 'destijl', title: { x: 2.4, y: -3.4, h: 1.6, rot: 0, ink: 'K' },
    s: `B -3.6 0.3 5.6 0.7 0 K 0 | B -4.2 0.3 0.7 7.2 0 K 0 | C -2.8 1.9 2.2 0 0 R 0 | Q 2.8 -3.6 3.8 0 0 Y 0 | C 4.8 1.9 1.8 0 0 B 0 | T 3.6 0.5 2.2 1.9 30 R 1 | B 4.0 2.9 4.6 0.3 -20 K | H -5.4 -2.4 1.8 0 0 B 0 0 3.1416 | S 2.6 1.4 0.7 0 0 R 2` },
  { name: 'soleil-noir', style: 'constructiviste', title: { x: -6.1, y: 3.35, h: 1.5, rot: 0, ink: 'R' },
    s: `C 3.8 0.4 4.6 0 0 K 0 | H 3.8 0.4 2.8 0 45 Y 1 0 3.1416 | B -3.6 1.6 5.2 0.5 -15 R 0 | B -3.6 0.6 4.0 0.5 -15 B 0 | B -3.6 -0.4 2.8 0.5 -15 Y 0 | C -4.8 -2.5 1.6 0 0 R 0 | B -0.8 -2.9 6.4 0.3 -10 K | T 0.4 2.9 1.2 0.9 180 B 2 | S 5.6 -3.0 0.6 0 45 R 1` },
  { name: 'rosace', style: 'kandinsky', title: { x: 2.3, y: -3.45, h: 1.3, rot: 0, ink: 'K' },
    s: `Q -4.0 -0.4 2.4 0 0 R 0 | Q -4.0 -0.4 2.4 0 90 Y 0 | Q -4.0 -0.4 2.4 0 180 B 0 | Q -4.0 -0.4 2.4 0 270 K 1 | C 4.4 1.2 3.2 0 0 Y 0 | C 4.8 -1.6 1.8 0 0 R 0 | B 3.6 2.9 4.2 0.4 0 B 0 | B -4.4 -2.95 5.0 0.3 25 K | O -4.0 -0.4 6.0 0 0 K 0 3 | S 2.5 -2.6 0.7 0 0 R 1` },
  { name: 'escalier', style: 'destijl', title: { x: -6.0, y: 3.3, h: 1.4, rot: 0, ink: 'K' },
    s: `B 2.9 2.2 2.0 0.8 0 R 0 | B 3.8 1.2 3.2 0.8 0 Y 0 | B 4.7 0.2 4.4 0.8 0 B 0 | B 5.4 -0.8 2.6 0.8 0 K 0 | C -3.6 0.0 4.0 0 0 Y 0 | C -3.6 0.0 1.8 0 0 R 1 | T -4.9 -2.8 1.8 1.4 0 K 0 | B 0.6 -2.9 7.0 0.3 -12 K | H 4.0 -2.6 1.8 0 0 R 2 0 3.1416 | S -2.0 2.9 0.7 0 0 R 1` },
  { name: 'lune', style: 'suisse', title: { x: 6.1, y: -3.2, h: 2.0, rot: 90, ink: 'K' },
    s: `T -3.8 -0.4 4.8 5.0 0 Y 0 | H 3.6 0.6 3.6 0 -90 B 0 0 3.1416 | C 4.4 -2.4 1.6 0 0 R 0 | B 2.2 0 0.2 7.0 0 K 0 | B -3.4 2.4 5.6 0.32 22 K | C -5.4 2.6 1.2 0 0 R 1 | Q 6.4 3.6 1.4 0 180 Y 0 | A 3.6 0.6 4.4 0 -90 K 0 2 3.1416 | S -2.4 -3.1 0.6 0 0 R 2` },
  { name: 'damier', style: 'destijl', title: { x: 2.3, y: 3.15, h: 1.5, rot: 0, ink: 'R' },
    s: `B -4.6 1.6 1.8 1.8 0 R 0 | B -3.0 0.0 1.8 1.8 0 K 0 | B -4.6 -1.6 1.8 1.8 0 Y 0 | C 4.0 -0.2 3.6 0 0 B 0 | B 4.0 -0.2 4.4 0.4 45 Y 1 | B 3.6 -3.0 4.6 0.3 -18 K | T -2.4 2.8 1.4 1.1 180 B 0 | H 5.6 -2.9 1.4 0 0 R 0 0 3.1416 | S 2.4 2.4 0.7 0 0 R 2` },
  { name: 'arc', style: 'kandinsky', title: { x: -6.1, y: -3.4, h: 1.5, rot: 0, ink: 'R' },
    s: `H -3.8 0.4 5.0 0 90 R 0 0 3.1416 | C -3.6 0.4 1.4 0 0 K 1 | B 3.8 -1.4 5.6 0.6 30 B 0 | C 4.8 1.9 2.2 0 0 Y 0 | C 2.6 1.6 0.8 0 0 R 0 | B -3.9 -2.4 5.6 0.3 -8 K | T 4.6 -3.0 1.4 1.0 0 K 0 | A 4.8 1.9 3.0 0 0 B 0 2 2.4 | S -2.2 2.9 0.7 0 0 R 1` },
  // LES QUATRE STYLES
  { name: 'constructiviste', style: 'constructiviste', title: { x: -6.2, y: -1.4, h: 2.4, rot: 20, ink: 'K' },
    s: `B -4.6 -0.9 6.0 1.0 20 R 0 | C 4.6 1.2 3.4 0 0 K 0 | B 3.2 -2.2 6.0 0.4 -35 K | T -4.6 2.4 2.4 2.0 200 R 0 | C -4.6 -2.6 1.6 0 0 K 1 | H 4.6 -2.8 2.0 0 0 R 0 0 3.1416 | S 2.6 2.8 0.7 0 20 R 2 | B 5.6 3.1 2.6 0.3 20 K 0` },
  { name: 'suisse', style: 'suisse', title: { x: -6.1, y: 2.6, h: 2.6, rot: -90, ink: 'K' },
    s: `O 3.8 0.2 5.0 0 0 K 0 6 | C 3.8 0.2 1.6 0 0 R 0 | B -3.0 -2.6 6.6 0.36 -30 K | C -3.4 0.6 2.4 0 0 B 0 | B 2.4 -3.0 3.0 0.4 0 R 0 | T 5.4 -2.6 1.4 1.2 0 Y 1 | H -3.4 2.8 1.8 0 180 Y 0 0 3.1416 | S 2.4 2.6 0.7 0 0 R 1` },
  { name: 'kandinsky', style: 'kandinsky', title: { x: 2.3, y: 3.2, h: 1.3, rot: 0, ink: 'K' },
    s: `C -3.6 0.4 3.6 0 0 B 0 | C -3.6 0.4 2.2 0 0 Y 0 | C -3.6 0.4 0.9 0 0 R 1 | O 4.2 0.8 3.2 0 0 K 0 4 | T 4.4 -2.2 2.0 1.8 15 Y 0 | B 0 -2.9 7.4 0.28 18 K | A -3.6 0.4 4.6 0 30 K 0 2 2.0 | H 2.6 2.6 1.4 0 0 R 0 0 3.1416 | S 5.4 2.8 0.6 0 0 R 2` },
  { name: 'destijl', style: 'destijl', title: { x: 2.4, y: -3.4, h: 1.6, rot: 0, ink: 'K' },
    s: `B -4.2 1.6 3.6 2.4 0 R 0 | B -2.7 -1.6 2.6 2.6 0 Y 0 | B 4.6 -1.0 3.2 3.8 0 B 0 | B -2.4 0 0.4 7.4 0 K 0 | B 2.6 0 0.4 7.4 0 K 0 | B 0 2.5 12.8 0.4 0 K 0 | B 4.4 2.6 4.4 0.36 -20 K | C 4.6 -1.0 1.2 0 0 Y 1 | S -5.6 -2.9 0.7 0 0 R 2 | T 3.6 2.6 1.0 0.8 0 Y 0` }
];

export function parseShapes(str) {
  return str.split('|').map((line, i) => {
    const t = line.trim().split(/\s+/);
    const s = { type: t[0], x: +t[1], y: +t[2], w: +t[3], h: +t[4], rot: +t[5], ink: t[6], level: t[7] != null ? +t[7] : 0, p1: t[8] != null ? +t[8] : 0, p2: t[9] != null ? +t[9] : 0, z: i };
    if (['C', 'R', 'O', 'H', 'A', 'S', 'K'].includes(s.type) || s.type === 'Q') s.h = s.w;
    return s;
  });
}

// ------------------------------------------------- le remplisseur de micro-éléments
const MICRO = {
  kandinsky: ['L', 'L', 'A', 'K', 'O', 'R', 'D', 'P', 'X', 'S', 'C'],
  suisse: ['O', 'D', 'D', 'L', 'R', 'S', 'C', 'X', 'F'],
  constructiviste: ['L', 'L', 'F', 'P', 'D', 'S', 'X', 'B'],
  destijl: ['L', 'S', 'K', 'D', 'B', 'X', 'P']
};
const STYLE_INKS = { kandinsky: ['B', 'Y', 'R', 'K', 'A'], suisse: ['K', 'R', 'K', 'B', 'A'], constructiviste: ['R', 'K', 'K', 'R', 'A'], destijl: ['R', 'Y', 'B', 'K', 'K'] };
function microShape(type, r, style) {
  const pick = (a) => a[Math.floor(r() * a.length)];
  const ink = pick(STYLE_INKS[style]);
  const rot = style === 'destijl' ? pick([0, 90]) : Math.round(r() * 23) * 15;
  switch (type) {
    case 'L': return { type, w: 1.2 + r() * 2.4, h: 0.06, rot, ink: 'K', p1: 1 + Math.round(r() * 2) };
    case 'A': return { type, w: 0.8 + r() * 0.8, h: 0, rot, ink: pick(['K', ink]), p1: 2, p2: 1.2 + r() * 2.4 };
    case 'K': return { type, w: 0.8 + r() * 0.4, h: 0, rot: style === 'destijl' ? 0 : rot, ink: 'K' };
    case 'O': return { type, w: 0.9 + r() * 0.7, h: 0, rot: 0, ink: pick(['K', ink]), p1: 3 + Math.floor(r() * 2) };
    case 'R': return { type, w: 0.6 + r() * 0.8, h: 0, rot: 0, ink: pick(['K', ink]), p1: 2 };
    case 'D': return { type, w: 1.6 + r() * 1.6, h: 0.2, rot: style === 'destijl' ? pick([0, 90]) : pick([0, 0, 90, 45, -45]), ink: pick(['K', 'R', 'B']), p1: 6 + Math.floor(r() * 4) };
    case 'P': return { type, w: 0.4 + r() * 0.3, h: 0, rot: pick([0, 45]), ink: 'K', p1: 3 };
    case 'X': return { type, w: 0.8 + r() * 0.6, h: 0.5 + r() * 0.5, rot, ink: 'K', p1: 6 };
    case 'S': return { type, w: 0.45 + r() * 0.35, h: 0, rot: pick([0, 15, 45]), ink: 'R' };
    case 'C': return { type, w: 0.3 + r() * 0.35, h: 0, rot: 0, ink: pick([ink, 'K', 'B']) };
    case 'F': return { type, w: 1.0 + r() * 0.8, h: 0.3, rot, ink: 'K', p1: 3 };
    case 'B': return { type, w: 0.6 + r() * 0.6, h: 0.12 + r() * 0.1, rot, ink: pick(['K', 'R']) };
  }
  return null;
}
// au moins 15 micro-éléments, et chaque instrument présent : rangée de points
// (charleys), petits carrés (accents), arcs ; le vide reste au-dessus de 25 %
export function fillMicro(base, style, seed, opts) {
  const r = rng(seed * 2654435761 + 7);
  const out = base.map((s) => Object.assign({}, s));
  const need = ['D', 'D', 'S', 'S', 'A'];
  let want = Math.max(15 + Math.floor(r() * 9), 27 + Math.floor(r() * 6) - base.filter((s) => !isMicro(s)).length);
  let tries = 0;
  const has = (t) => out.filter((s) => s.type === t && isMicro(s)).length;
  while ((out.filter(isMicro).length < want || need.some((t, i) => has(t) < need.filter((x, j) => x === t && j <= i).length)) && tries++ < 3000) {
    const missing = need.find((t, i) => has(t) < need.filter((x, j) => x === t && j <= i).length);
    const type = missing || MICRO[style][Math.floor(r() * MICRO[style].length)];
    const m = microShape(type, r, style);
    if (!m) continue;
    m.x = (r() * 2 - 1) * (SCREEN[0] - 0.4); m.y = (r() * 2 - 1) * (SCREEN[1] - 0.4); m.level = r() < 0.15 ? 1 : 0; m.z = out.length;
    if (m.h === 0) m.h = m.w;
    if (!clearOfReserve(m)) continue;
    // pas sur la typographie (elle vient après) : on garde les micro-éléments hors de la bande du titre
    if (opts && opts.avoid && opts.avoid(m)) continue;
    out.push(m);
  }
  return out;
}

// l'analyse : le vide (hors case de la pochette, sur l'écran), le compte des éléments
export function analyse(comp, n) {
  const N = n || 96, M = Math.round(N * SCREEN[1] / SCREEN[0]);
  let free = 0, covered = 0;
  for (let j = 0; j < M; j++) for (let i = 0; i < N; i++) {
    const x = -SCREEN[0] + (i + 0.5) / N * 2 * SCREEN[0], y = -SCREEN[1] + (j + 0.5) / M * 2 * SCREEN[1];
    if (Math.abs(x) < RESERVE[0] && Math.abs(y) < RESERVE[1]) continue;
    free++;
    if (comp.some((s) => s.type !== 'G' && sdf(s, x, y, 20) < 0)) covered++;
  }
  const els = comp.filter((s) => s.type !== 'G');
  return { void: 1 - covered / free, elements: els.length, micro: els.filter(isMicro).length, large: els.filter(isLarge).length, diagonal: els.some(isThickDiagonalRule) };
}

// la typographie : la zone qu'occupera le titre (pour que les micro-éléments l'évitent)
export function titleBand(t) {
  const a = t.rot * Math.PI / 180, dx = Math.cos(a), dy = Math.sin(a), len = 9;
  return (m) => {
    const vx = m.x - t.x, vy = m.y - t.y;
    const along = vx * dx + vy * dy, across = -vx * dy + vy * dx;
    return along > -0.5 && along < len && across > -0.3 && across < t.h + 0.3;
  };
}

// une forme écrite à la main qui mord sur la case de la pochette est poussée
// vers l'extérieur (horizontalement, du côté où elle est) jusqu'à en sortir
export function nudgeOut(s) {
  const dir = s.x < 0 ? -1 : 1;
  for (let k = 0; k < 80 && !clearOfReserve(s); k++) s.x += dir * 0.05;
  return s;
}

const GROW = 1.18;
// le titre écrit à la main (x, y, rot) -> la spécification de mise en page :
// côté de l'affiche, sens de lecture, ancrage haut/bas, hauteur de lettre
function titleSpec(t) {
  const rot = t.rot === 90 || t.rot === -90 || t.rot === 0 ? t.rot : t.rot;
  return { region: t.x < 0 ? 'L' : 'R', rot, anchor: t.y > 0 ? 'top' : 'bottom', h: t.h, ink: t.ink, initial: !!t.initial };
}

export const POSTERS = STRUCT.map((p, i) => {
  // la refonte veut de la densité : formes structurantes agrandies de 18 %
  // (positions inchangées), les superpositions qui en naissent sont voulues
  const base = parseShapes(p.s).map((s) => { if (!isMicro(s) && s.type !== 'L' && !(s.type === 'B' && s.w / s.h >= 4)) { s.w *= GROW; if (s.type !== 'B' || Math.min(s.w, s.h) > 0.5) s.h *= GROW; } return nudgeOut(s); });
  const shapes = fillMicro(base, p.style, 1000 + i, { avoid: titleBand(p.title) });
  // la lettrine partout sauf sur les titres en diagonale (une affiche sur trois la garde sans, pour varier)
  if (i % 3 !== 2) p.title.initial = true;
  return { name: p.name, style: p.style, title: titleSpec(p.title), shapes };
});

// LE GÉNÉRATEUR : une structure selon un style, puis le remplisseur
const STYLES = ['kandinsky', 'suisse', 'constructiviste', 'destijl'];
export function generatePoster(seed) {
  const r = rng(seed * 7919 + 13);
  const pick = (a) => a[Math.floor(r() * a.length)];
  for (let attempt = 0; attempt < 300; attempt++) {
    const style = pick(STYLES);
    const inks = STYLE_INKS[style];
    const shapes = [];
    const place = (s) => {
      for (let t = 0; t < 120; t++) {
        const side = r() < 0.5 ? -1 : 1;
        s.x = side * (RESERVE[0] + 0.4 + r() * (SCREEN[0] - RESERVE[0])); s.y = (r() * 2 - 1) * SCREEN[1];
        if (clearOfReserve(s)) { s.z = shapes.length; shapes.push(s); return true; }
      }
      return false;
    };
    const rot = () => style === 'destijl' ? 0 : Math.round(r() * 23) * 15;
    // 3 à 5 grandes (une domine, souvent coupée par le bord), 8 à 12 moyennes
    const nLarge = 3 + Math.floor(r() * 3), nMed = 8 + Math.floor(r() * 5);
    let ok = true;
    for (let k = 0; k < nLarge && ok; k++) {
      const type = style === 'destijl' ? 'B' : pick(['C', 'C', 'H', 'T', 'Q', 'O']);
      const w = (k === 0 ? 4.4 : 3.0) + r() * 1.2;
      const s = { type, w, h: type === 'B' ? w * (0.5 + r() * 0.6) : (type === 'T' ? w * 0.9 : w), rot: rot(), ink: pick(inks), level: 0, p1: type === 'O' ? 4 + Math.floor(r() * 3) : 0, p2: type === 'H' ? Math.PI : 0 };
      if (type === 'C') s.ink = pick(['B', 'B', 'K', 'R']);
      if (type === 'T') s.ink = 'Y';
      ok = place(s);
    }
    for (let k = 0; k < nMed && ok; k++) {
      const type = pick(['C', 'T', 'B', 'H', 'A', 'B']);
      const w = 1.2 + r() * 1.4;
      const s = { type, w, h: type === 'B' ? (r() < 0.5 ? 0.35 : w * 0.6) : (type === 'T' ? w * 0.85 : w), rot: rot(), ink: pick(inks), level: r() < 0.3 ? 1 + Math.floor(r() * 2) : 0, p1: type === 'A' ? 3 : 0, p2: type === 'H' || type === 'A' ? 1.6 + r() * 1.5 : 0 };
      if (type === 'B' && s.h < 0.5) s.w = 3.6 + r() * 2;            // une longue barre : la basse
      if (type === 'C') s.ink = 'B'; if (type === 'T') s.ink = 'Y';
      ok = place(s);
    }
    // une ou deux règles noires épaisses en diagonale
    for (let k = 0; k < 1 + Math.floor(r() * 2) && ok; k++) ok = place({ type: 'B', w: 5 + r() * 2.5, h: 0.32, rot: pick([20, 30, -30, -20, 45, -45, 60]), ink: 'K', level: 0 });
    if (!ok) continue;
    const title = { x: pick([-6.1, 2.3]), y: pick([3.2, -3.4]), h: 1.3 + r() * 0.6, rot: style === 'constructiviste' ? pick([0, 20, 30]) : 0, ink: pick(['K', 'R', 'K']) };   // (noir ou rouge : un titre bleu disparaissait sur les cercles bleus du kick)
    if (r() < 0.4) Object.assign(title, { x: pick([-6.1, 6.1]), y: -3.3, h: 1.8 + r() * 0.6 }, { rot: 0 }), title.rot = title.x < 0 ? 90 : -90;
    title.initial = r() < 0.7;
    const all = fillMicro(shapes, style, seed, { avoid: titleBand(title) });
    const a = analyse(all, 64);
    if (a.void < 0.27 || a.elements < 25 || a.elements > 40 || a.micro < 15 || !a.diagonal) continue;
    return { name: 'générée-' + seed, style, title: titleSpec(title), shapes: all };
  }
  return POSTERS[seed % POSTERS.length];
}


// ------------------------------------------------------- la typographie
// Les capitales vivent dans la colonne libre d'un côté de la pochette (R6 :
// hors des zones d'interface du haut, hors de la case de la pochette).
// Lignes empilées ; la hauteur part de celle de l'affiche et descend jusqu'à
// ce que le titre tienne ; au plus bas, on garde les lignes qui tiennent.
export const LINE = 1.12;                 // interligne, en hauteurs de lettre
export function titleRegion(side, halfW) {
  const hw = halfW || SCREEN[0];
  return side === 'L' ? { x0: -hw + 0.12, x1: -RESERVE[0] - 0.3, y0: -SCREEN[1] + 0.1, y1: 2.6 }
                      : { x0: RESERVE[0] + 0.3, x1: hw - 0.12, y0: -2.75, y1: 2.6 };
}
// zones d'interface (R6), en colonnes, pour un demi-écran halfW x SCREEN[1]
export function uiZones(halfW) {
  const W = 2 * halfW, Hh = 2 * SCREEN[1], top = SCREEN[1], bot = -SCREEN[1];
  return [
    { x0: halfW - 0.22 * W, x1: halfW, y0: top - 0.12 * Hh, y1: top },        // Broadcast
    { x0: -halfW, x1: -halfW + 0.08 * W, y0: top - 0.12 * Hh, y1: top },      // micro
    { x0: halfW - 0.22 * W, x1: halfW, y0: bot, y1: bot + 0.10 * Hh },        // navigation
    { x0: -0.20 * W, x1: 0.20 * W, y0: top - 0.10 * Hh, y1: top }             // nom de la station
  ];
}
function boxCorners(g) {
  const a = g.rot * Math.PI / 180, c = Math.cos(a), s = Math.sin(a), out = [];
  for (const [u, v] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) { const lx = u * g.w / 2, ly = v * g.h / 2; out.push([g.x + lx * c - ly * s, g.y + lx * s + ly * c]); }
  return out;
}
function boxHits(corners, b) {
  // séparation d'axes simplifiée : boîte englobante des coins contre la zone
  const xs = corners.map((p) => p[0]), ys = corners.map((p) => p[1]);
  return Math.max(...xs) > b.x0 && Math.min(...xs) < b.x1 && Math.max(...ys) > b.y0 && Math.min(...ys) < b.y1;
}
export function glyphOk(g, halfW) {
  const k = boxCorners(g);
  if (boxHits(k, { x0: -RESERVE[0] - 0.1, x1: RESERVE[0] + 0.1, y0: -RESERVE[1] - 0.1, y1: RESERVE[1] + 0.1 })) return false;
  for (const z of uiZones(halfW)) if (boxHits(k, z)) return false;
  return true;
}
function wrap(words, maxW, h, widthOf, allowSplit) {
  const lines = []; let cur = '';
  const w = (s) => { let t = 0; for (const ch of s) t += ch === ' ' ? 0.36 * h : (widthOf(ch) + 0.12) * h; return t - 0.12 * h; };
  for (const word of words) {
    let wd = word;
    if (!allowSplit && w(wd) > maxW) return null;   // on réduit la lettre avant de couper un mot
    while (w(wd) > maxW && wd.length > 1) {        // un mot trop long se coupe (les lettres continuent à la ligne)
      let k = wd.length; while (k > 1 && w(wd.slice(0, k)) > maxW) k--;
      if (cur) { lines.push(cur); cur = ''; }
      lines.push(wd.slice(0, k)); wd = wd.slice(k);
    }
    const cand = cur ? cur + ' ' + wd : wd;
    if (w(cand) <= maxW) cur = cand; else { if (cur) lines.push(cur); cur = wd; }
  }
  if (cur) lines.push(cur);
  return lines;
}
// -> [{ ch, x, y, w, h, rot, line, i }] en colonnes (centres des boîtes de glyphes)
export function layoutTitle(text, spec, widthOf, halfW, region) {
  const words = String(text || '').split(' ').filter(Boolean);
  if (!words.length) return [];
  const R = region || titleRegion(spec.region, halfW);
  const rw = R.x1 - R.x0, rh = R.y1 - R.y0;
  const vertical = spec.rot === 90 || spec.rot === -90;
  const diag = !vertical && spec.rot !== 0;
  const lenAvail = (vertical ? rh : rw) * (diag ? 0.92 : 1), depthAvail = vertical ? rw : rh;
  let best = null;
  const hMin = spec.hMin || 0.6;
  for (let h = spec.h; h >= hMin; h -= 0.05) {
    const last = h - 0.05 < hMin;
    const lines = wrap(words, lenAvail, h, widthOf, false) || (last ? wrap(words, lenAvail, h, widthOf, true) : null);
    if (!lines) continue;
    const depth = (lines.length - 1) * h * LINE + h;
    const fits = depth <= depthAvail * (diag ? 0.8 : 1);
    const glyphs = place(lines, h);
    if (fits && glyphs.every((g) => glyphOk(g, halfW || SCREEN[0]))) { best = glyphs; break; }
    if (last) {
      // au plus petit : on garde les lignes entières qui tiennent
      const keep = [];
      for (let n = lines.length; n >= 1; n--) { const g = place(lines.slice(0, n), h); if ((n - 1) * h * LINE + h <= depthAvail && g.every((q) => glyphOk(q, halfW || SCREEN[0]))) { keep.push(...g); break; } }
      best = keep;
    }
  }
  return best || [];

  function place(lines, h) {
    const out = [];
    const a = spec.rot * Math.PI / 180, c = Math.cos(a), s = Math.sin(a);
    const depth = (lines.length - 1) * h * LINE + h;
    let lineW = 0;
    // le bloc dans son repère : ligne i, ligne de base en by = -i * h * LINE ; bx vers la droite
    const raw = [];
    lines.forEach((ln, i) => {
      let bx = 0;
      for (const ch of ln) {
        if (ch === ' ') { bx += 0.36 * h; continue; }
        const gw = widthOf(ch) * h;
        raw.push({ ch, bx: bx + gw / 2, by: -i * h * LINE + h / 2, w: gw, line: i });
        bx += gw + 0.12 * h;
      }
      lineW = Math.max(lineW, bx - 0.12 * h);
    });
    // l'origine du bloc (coin haut-gauche de la première ligne = (0, h) dans le repère)
    let ox, oy;
    const bleed = (spec.bleed != null ? spec.bleed : 0.16) * h;                 // la première ligne mord sur le bord de l'écran
    if (spec.rot === 90) { ox = R.x0 + h - bleed; oy = spec.anchor === 'top' ? R.y1 - lineW : R.y0; }
    else if (spec.rot === -90) { ox = R.x1 - h + bleed; oy = spec.anchor === 'top' ? R.y1 : R.y0 + lineW; }
    else if (!diag) { ox = spec.region === 'L' ? R.x0 : R.x1 - lineW; oy = spec.anchor === 'top' ? R.y1 - h : R.y0 + depth - h; }
    else {
      // en diagonale : le centre du bloc au centre de la région
      const cx = lineW / 2, cy = h - depth / 2, mx = (R.x0 + R.x1) / 2, my = (R.y0 + R.y1) / 2;
      ox = mx - (cx * c - cy * s); oy = my - (cx * s + cy * c);
    }
    for (const g of raw) {
      // repère du bloc -> écran : rotation de rot ; pour ±90 la ligne de base court le long de y
      const x = ox + g.bx * c - g.by * s, y = oy + g.bx * s + g.by * c;
      out.push({ ch: g.ch, x, y, w: g.w, h, rot: spec.rot, line: g.line, i: out.length });
    }
    return out;
  }
}

// LA LETTRINE : la première lettre, monumentale (jusqu'à 38 % de la hauteur
// de l'écran), coupée par le bord ; le reste du titre se compose dans ce qui
// reste de la colonne. Sans lettrine (ou en diagonale) : layoutTitle seul.
export function layoutPosterTitle(text, spec, widthOf, halfW) {
  // l'orientation de l'affiche d'abord ; si le titre y devient petit, la
  // verticale de son côté (90° à gauche, -90° à droite) quand elle le grandit
  const n = String(text || '').replace(/ /g, '').length;
  const score = (g) => g.length ? g.reduce((a, q) => a + q.h, 0) / g.length * (g.length / Math.max(1, n)) : 0;
  const a = layoutOne(text, spec, widthOf, halfW);
  if (score(a) >= 1.0 || spec.rot === 90 || spec.rot === -90) return a;
  const alt = Object.assign({}, spec, { rot: spec.region === 'L' ? 90 : -90, anchor: 'bottom' });
  const b = layoutOne(text, alt, widthOf, halfW);
  return score(b) > score(a) * 1.15 ? b : a;
}
function layoutOne(text, spec, widthOf, halfW) {
  const diag = spec.rot !== 0 && spec.rot !== 90 && spec.rot !== -90;
  const t = String(text || '').trim();
  if (!spec.initial || diag || t.replace(/ /g, '').length < 3) return layoutTitle(t, spec, widthOf, halfW);
  const R = titleRegion(spec.region, halfW);
  const first = t[0], rest = t.slice(1).replace(/^ +/, '');
  for (let hb = 2.75; hb >= 1.8; hb -= 0.1) {
    const big = layoutTitle(first, Object.assign({}, spec, { h: hb, hMin: hb, bleed: 0.2 }), widthOf, halfW);
    if (!big.length) continue;
    const g = big[0], gap = 0.25;
    const r2 = Object.assign({}, R);
    if (spec.rot === 0) { if (spec.anchor === 'top') r2.y1 = g.y - hb / 2 - gap; else r2.y0 = g.y + hb / 2 + gap; }
    else if (spec.rot === 90) r2.x0 = g.x + hb / 2 + gap;
    else r2.x1 = g.x - hb / 2 - gap;
    if (r2.x1 - r2.x0 < 0.8 || r2.y1 - r2.y0 < 0.8) continue;
    const others = layoutTitle(rest, Object.assign({}, spec, { h: Math.min(spec.h, 1.3), bleed: 0 }), widthOf, halfW, r2);
    if (!others.length && rest.length) continue;
    big[0].initial = true;
    return big.concat(others.map((o, k) => Object.assign(o, { i: k + 1, line: o.line + 1 })));
  }
  return layoutTitle(t, spec, widthOf, halfW);
}
